import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  Shield,
  Mail
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="privacy-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
              <Shield className="h-8 w-8 text-green-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Privacy Policy
              </h1>
              <p className="text-slate-500">Last updated: March 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none">
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12 space-y-8">
              
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  1. Information We Collect
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Gifted Academy Tutors collects personal information necessary to provide tutoring services, including:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Student information (grade level, subjects of interest)</li>
                  <li>Parent/guardian contact information (for students under 18)</li>
                  <li>Session history and lesson notes</li>
                  <li>Communication messages (live chat, emails)</li>
                  <li>Payment records processed through Stripe</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  User data is used only for service delivery and communication. Specifically, we use your information to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Provide tutoring services and match students with appropriate tutors</li>
                  <li>Process payments and manage your account</li>
                  <li>Send lesson notes and educational materials</li>
                  <li>Communicate important updates about sessions and services</li>
                  <li>Improve our services and user experience</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  3. Information Sharing
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We do not sell your personal information to third parties. We may share your information with:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                  <li>Tutors assigned to your sessions (limited to necessary educational information)</li>
                  <li>Payment processors (Stripe) to process transactions</li>
                  <li>Service providers who assist in operating our platform</li>
                  <li>Legal authorities when required by law</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  4. Data Security
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest, secure password policies, and regular security assessments.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  5. Data Retention
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Session records and lesson notes are retained for the duration of your account and for a reasonable period afterward to allow for continuity of service.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  6. Your Privacy Rights
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Users may request access, correction, or deletion of their personal data by contacting us. Under applicable privacy laws, you have the right to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Withdraw consent for data processing</li>
                  <li>Receive a copy of your data in a portable format</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  7. Compliance with Canadian Privacy Laws
                </h2>
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <p className="text-slate-700 leading-relaxed">
                    Gifted Academy Tutors complies with applicable Canadian privacy laws including the <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong>. We are committed to protecting your privacy and handling your personal information responsibly.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  8. Cookies and Tracking
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Our platform uses cookies and similar technologies to enhance your experience, remember your preferences, and analyze how our services are used. You can control cookie settings through your browser preferences.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  9. Children's Privacy
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  For students under 18, we require parent or guardian consent before collecting and processing personal information. Parents and guardians have the right to review, modify, or request deletion of their child's information.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  10. Contact Us
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  For questions about this Privacy Policy or to exercise your privacy rights, please contact us at:
                </p>
                <div className="mt-4 flex items-center gap-2 text-indigo-700">
                  <Mail className="h-5 w-5" />
                  <a href="mailto:contact@giftedacademytutors.ca" className="font-medium hover:underline">
                    contact@giftedacademytutors.ca
                  </a>
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
